/**
 * AchievementNotifier.ts — Achievement unlock notification display.
 *
 * Provides:
 * - Queue-based notification system for sequential display
 * - ASCII art styled notification boxes
 * - Auto-fade after configurable duration
 * - Hidden achievement reveal on unlock
 */

import type { Achievement } from '@game/data/types';
import type { Terminal } from './Terminal';

// --- Types --------------------------------------------------------------

export interface AchievementNotification {
    id: string;
    name: string;
    description: string;
    icon?: string;
    hidden: boolean;
    timestamp: number;
}

export interface NotificationConfig {
    /** Duration to display each notification (ms) */
    displayDuration: number;
    /** Position on screen */
    position: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
    /** Width of notification box in characters */
    width: number;
}

const DEFAULT_CONFIG: NotificationConfig = {
    displayDuration: 5000,
    position: 'top-right',
    width: 40,
};

// --- Notification Manager -----------------------------------------------

export class NotificationManager {
    private queue: AchievementNotification[] = [];
    private currentNotification: AchievementNotification | null = null;
    private displayStartTime: number = 0;
    private config: NotificationConfig;
    private terminal: Terminal | null = null;

    constructor(config: Partial<NotificationConfig> = {}) {
        this.config = { ...DEFAULT_CONFIG, ...config };
    }

    /** Set the terminal for rendering */
    setTerminal(terminal: Terminal): void {
        this.terminal = terminal;
    }

    /** Queue a notification for display */
    show(notification: AchievementNotification): void {
        this.queue.push(notification);

        // If nothing is currently showing, start displaying
        if (!this.currentNotification) {
            this.showNext();
        }
    }

    /** Create a notification from an achievement definition */
    showAchievement(def: Achievement): void {
        const notification: AchievementNotification = {
            id: def.id,
            name: def.name,
            description: def.description,
            icon: def.icon,
            hidden: def.isHidden ?? false,
            timestamp: Date.now(),
        };
        this.show(notification);
    }

    /** Get current notification being displayed */
    current(): AchievementNotification | null {
        return this.currentNotification;
    }

    /** Dismiss current notification early */
    dismiss(): void {
        this.currentNotification = null;
        this.showNext();
    }

    /** Check if notifications are queued */
    hasPending(): boolean {
        return this.queue.length > 0;
    }

    /** Get number of pending notifications */
    pendingCount(): number {
        return this.queue.length;
    }

    /** Update notification state (call each frame) */
    update(): void {
        if (!this.currentNotification) return;

        const elapsed = Date.now() - this.displayStartTime;
        if (elapsed >= this.config.displayDuration) {
            this.dismiss();
        }
    }

    /** Render the current notification */
    render(): string[] {
        if (!this.currentNotification) return [];

        const { name, description, icon } = this.currentNotification;
        const width = this.config.width;
        const lines: string[] = [];

        // Top border
        lines.push('╔' + '═'.repeat(width - 2) + '╗');

        // Header
        const header = ' 🏆 ACHIEVEMENT UNLOCKED ';
        const headerPad = Math.floor((width - 2 - header.length) / 2);
        lines.push('║' + ' '.repeat(headerPad) + header + ' '.repeat(width - 2 - headerPad - header.length) + '║');

        // Separator
        lines.push('╠' + '═'.repeat(width - 2) + '╣');

        // Icon (if available)
        if (icon) {
            const iconLine = ` [${icon}] `;
            lines.push('║' + iconLine.padEnd(width - 2) + '║');
        }

        // Name
        const nameLine = ` ${name} `;
        lines.push('║' + nameLine.padEnd(width - 2) + '║');

        // Description (word wrap)
        const descWords = description.split(' ');
        let descLine = ' ';
        for (const word of descWords) {
            if (descLine.length + word.length + 1 > width - 3) {
                lines.push('║' + descLine.padEnd(width - 2) + '║');
                descLine = ' ' + word;
            } else {
                descLine += (descLine.length > 1 ? ' ' : '') + word;
            }
        }
        if (descLine.length > 1) {
            lines.push('║' + descLine.padEnd(width - 2) + '║');
        }

        // Bottom border
        lines.push('╚' + '═'.repeat(width - 2) + '╝');

        return lines;
    }

    /** Render to terminal centered on screen */
    renderToTerminal(): void {
        if (!this.terminal || !this.currentNotification) return;

        const lines = this.render();
        const { width } = this.config;
        const { cols: termWidth, rows: termHeight } = this.terminal.getSize();

        // Center the notification box on screen
        const startX = Math.max(1, Math.floor((termWidth - width) / 2));
        const startY = Math.max(1, Math.floor((termHeight - lines.length) / 2));

        // Render each line
        for (let i = 0; i < lines.length; i++) {
            this.terminal.setCursor(startY + i, startX);
            this.terminal.write(lines[i]);
        }
    }

    /** Reset the notification manager */
    reset(): void {
        this.queue = [];
        this.currentNotification = null;
    }

    // --- Private Methods --------------------------------------------------

    private showNext(): void {
        if (this.queue.length === 0) {
            this.currentNotification = null;
            return;
        }

        this.currentNotification = this.queue.shift()!;
        this.displayStartTime = Date.now();
    }
}

// --- Factory ------------------------------------------------------------

let notificationManagerInstance: NotificationManager | null = null;

/**
 * Gets the global notification manager instance.
 */
export function getNotificationManager(): NotificationManager {
    if (!notificationManagerInstance) {
        notificationManagerInstance = new NotificationManager();
    }
    return notificationManagerInstance;
}

/**
 * Resets the notification manager instance (for testing).
 */
export function resetNotificationManager(): void {
    notificationManagerInstance = null;
}
